import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, LogIn, Loader2, KeyRound } from 'lucide-react';

const API_URL = '/api';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  useEffect(() => {
    if (localStorage.getItem('token')) navigate('/');
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/login`, 
        { email: email.trim(), password: password.trim() },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data.success) {
        localStorage.setItem('token', res.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        navigate('/');
        window.location.reload();
      }
    } catch (err) {
      if (!err.response) {
        setError('Cannot connect to server. Please ensure the backend is running on port 5000.');
      } else {
        setError(err.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md shadow-2xl border border-white/20">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">Email Automation</h1>
          <p className="text-gray-300 mt-2">Sign in to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="mt-6 text-center text-gray-400 text-sm space-y-2">
          <span className="block">
            Don't have an account?{' '}
            <Link to="/register" className="text-purple-400 hover:text-purple-300 font-medium">
              Register
            </Link>
          </span>
          <button
            type="button"
            onClick={() => { setShowReset(true); setResetEmail(email); setResetMessage(''); }}
            className="text-purple-400 hover:text-purple-300 font-medium text-sm"
          >
            Forgot Password?
          </button>
        </p>
      </div>

      {showReset && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Reset Password
            </h3>
            {resetMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${resetMessage.includes('success') ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>
                {resetMessage}
              </div>
            )}
            <input
              type="email"
              placeholder="Your email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full mb-3 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white"
            />
            <input
              type="password"
              placeholder="New password (min 6 chars)"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className="w-full mb-4 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white"
            />
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!resetEmail || resetPassword.length < 6) {
                    setResetMessage('Enter email and new password (min 6 characters)');
                    return;
                  }
                  setResetLoading(true);
                  setResetMessage('');
                  try {
                    const r = await axios.post(`${API_URL}/auth/reset-password`, 
                      { email: resetEmail.trim(), newPassword: resetPassword.trim() });
                    if (r.data.success) {
                      setResetMessage('Password reset! You can now login.');
                      setTimeout(() => { setShowReset(false); setResetPassword(''); }, 2000);
                    }
                  } catch (err) {
                    setResetMessage(err.response?.data?.message || 'Reset failed');
                  } finally {
                    setResetLoading(false);
                  }
                }}
                disabled={resetLoading}
                className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
              <button
                onClick={() => { setShowReset(false); setResetMessage(''); setResetPassword(''); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
